import axiosHttpClient from '#helpers/axiosHttpClient.js'
import handleApiError from '#helpers/logging/handleApiError.js'
import Logger from '#helpers/logging/Logger.js'
import { url_featuretoggles } from '#src/constants/endpoints.js'
import { defaultExpiresTime, errorRetryTime, nowTime } from '#stores/storeDefaults.js'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'

export const useFeaturesStore = defineStore('features', () => {
	const features = useStorage('features', [], sessionStorage)
	const loading = shallowRef(false)
	const expires = useStorage('features-expires', null, sessionStorage)

	/**
	 * Check whether a certain feature is currently active.
	 * @param {string} featureName - The name of the feature to check
	 * @returns {boolean} - Whether the feature is currently active
	 */
	function isActive(featureName) {
		fetchFeatures()
		const feature = features.value.find(feature => feature.name === featureName)
		return feature?.active === true
	}

	const list = computed(() => {
		fetchFeatures()
		return features.value
	})

	/**
	 * Fetch all the features.
	 * @param {boolean} [force] - Whether to force fetching the features
	 * @returns {Promise} - A promise to act upon
	 */
	async function fetchFeatures(force = false) {
		if (force) {
			loading.value = false
		}
		if (loading.value) {
			return
		}
		if (new Date().getTime() < expires.value && !force) {
			return
		}
		// const auth = useAuthorizationStore()
		// if (!auth.accessToken) { return }

		loading.value = true
		await axiosHttpClient
			.get(url_featuretoggles)
			.then(response => {
				features.value = response?.data ?? []
				expires.value = nowTime() + defaultExpiresTime
			})
			.catch(e => {
				Logger.debug('useFeaturesStore: fetch failed')
				expires.value = nowTime() + errorRetryTime
				handleApiError(e)
			})
			.finally(() => {
				loading.value = false
			})
	}

	/**
	 * Clean up the data.
	 */
	function remove() {
		loading.value = false
		features.value = []
		expires.value = 0
	}

	return { features, loading, expires, isActive, list, fetch: fetchFeatures, remove }
})
